import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { calculateRecipeCostDisplay, resolveRecipeScalingContext } from '../utils/recipePricing';
import { resolveMatchDisplay } from '../utils/matchDisplay';

// --- PANTRY STAPLES REMOVED (Relies on Backend ID Matching) ---
const costHydrationCache = new Map();

const resolveImageSrc = (rawUrl) => {
  const value = String(rawUrl || '').trim();
  if (!value) return '';
  if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:')) return value;

  // Frontend-bundled static assets should stay on frontend origin.
  if (value.startsWith('/static/') || value.startsWith('/assets/') || value.startsWith('/images/')) return value;

  // API upload/media paths should go through API base.
  if (value.startsWith('/uploads/') || value.startsWith('/api/uploads/')) return `${API_BASE_URL}${value}`;
  if (value.startsWith('uploads/')) return `${API_BASE_URL}/${value}`;

  // Safe default for relative asset-like paths.
  if (value.startsWith('/')) return value;
  return `${API_BASE_URL}/${value}`;
};

const processText = (text, userProfile) => {
  if (!text || typeof text !== 'string') return text;
  const babyName = userProfile?.babyName || userProfile?.bebek_ismi || 'Bebek';
  return text.replace(/\{bebek[_\s]?[iı]smi\}/gi, babyName)
    .replace(/bebek_ismi/gi, babyName);
};

const RecipeCard = ({
  recipe,
  onCardClick,
  isSelected = false,
  onToggleSelect = null,
  hidePrimaryAction = false,
  hideActionButtons = false,
  hideSavingsPanel = false,
  hideCostComparison = false,
  isCompact = false,
  isModern = false,
  isDarkMode = false,
  userProfile = null,
  userPortion = 4,
  highlightAdaptive = false,
  showTopInsight = true
}) => {
  const [isPulsing, setIsPulsing] = useState(true);
  const [hydratedCostTotal, setHydratedCostTotal] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsPulsing(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const { presentation = {} } = recipe;
  const { state = 'cookable' } = presentation;

  const handleCardClick = (e) => {
    if (onCardClick) onCardClick(recipe);
  };

  const handleCheckboxClick = (e) => {
    if (e) e.stopPropagation();
    if (onToggleSelect) onToggleSelect(recipe);
  };

  // Intelligence Extraction
  const resolvedMatch = recipe.match || {};
  const canonical = recipe.canonical || {};
  const title = canonical.name || recipe.title || recipe.name || recipe.title;
  const imageUrl = canonical.imageUrl || recipe.imageUrl || recipe.image_url || recipe.image;
  const { calories, cookTime, cookingTime, difficulty = 'Kolay' } = recipe;
  const healthBadges = Array.isArray(recipe.healthBadges)
    ? recipe.healthBadges
    : (Array.isArray(recipe.health?.badges) ? recipe.health.badges : []);
  const healthReason = recipe.healthReason || recipe.health?.reason || null;
  // Faz 4: Sağlık+ uyarısı — tarif aktif modla çelişiyorsa (engellemez, sadece uyarır)
  const healthWarning = recipe.healthWarning || recipe.health?.warning || null;
  const matchDisplay = resolveMatchDisplay(recipe);
  const currentMatchPerc = matchDisplay.matchPercentage;
  const missingCount = matchDisplay.missingCount;
  const currentIsFullMatch = missingCount === 0 && (resolvedMatch.isFull ?? matchDisplay.isFull);
  const recommendationReasonList = Array.isArray(recipe?.recommendationExplain?.reasons)
    ? recipe.recommendationExplain.reasons.map((r) => String(r || '').trim()).filter(Boolean)
    : [];
  const conciseWhyLabel = (() => {
    if (missingCount > 0) return 'Eksik Var';
    if (currentIsFullMatch || currentMatchPerc >= 95) return 'Uyumlu';
    const first = recommendationReasonList[0]?.toLocaleLowerCase('tr-TR') || '';
    if (first.includes('hız') || first.includes('süre')) return 'Hızlı';
    if (first.includes('fiyat') || first.includes('maliyet') || first.includes('bütçe')) return 'Ekonomik';
    if (first.includes('alerjen') || first.includes('güvenli')) return 'Güvenli';
    return 'Öneri';
  })();
  const rescueDaysLeft = resolvedMatch.minDaysLeft ?? recipe.minDaysLeft;
  const isRescue = rescueDaysLeft !== undefined && rescueDaysLeft !== null && rescueDaysLeft <= 5;
  const recoveryAmount = recipe.recoveryAmount || 0;

  // State styling
  const currentStyles = {
    cookable: 'bg-white dark:bg-[#0f1115] border-slate-200 dark:border-white/5',
    missing: 'bg-slate-50 dark:bg-[#0f1115] border-slate-200 dark:border-white/5 opacity-95',
    rescue: 'bg-gradient-to-br from-rose-50 to-white dark:from-rose-950/10 dark:to-[#0f1115] border-rose-100 dark:border-rose-900/20',
    warning: 'bg-red-50/30 dark:bg-red-900/10 border-red-100 dark:border-red-900/30'
  };

  const activeState = isRescue ? 'rescue' : state;
  const showSmartHeader = showTopInsight && (isRescue || recoveryAmount > 0);
  const isAdaptiveRecipe = highlightAdaptive && !!(recipe.isAdapted || recipe.facts?.isAdapted || recipe.presentation?.isAdapted);

  // [V12.110] Law: Smart Multiplier for pre-scaled vs raw data
  const currentPortion = Number(userPortion) || 1;
  const scaling = resolveRecipeScalingContext(recipe, currentPortion);
  const isPreScaled = scaling.isPreScaled;
  const baseSrv = scaling.baseServings;
  const appliedPortion = scaling.appliedPortion;
  const displayPortionLabel = Number(currentPortion) || Number(appliedPortion) || Number(recipe?.presentation?.appliedPortion) || 1;

  const multiplier = scaling.effectiveMultiplier;
  const { displayedCostTotal } = calculateRecipeCostDisplay(recipe, currentPortion);
  const compactPriceSource = String(recipe?.priceSnapshot?.source || '').trim().toUpperCase();
  const hasSnapshotBreakdown = Array.isArray(recipe?.priceSnapshot?.breakdown) && recipe.priceSnapshot.breakdown.length > 0;
  const shouldHydrateCost =
    recipe?.sourceOfTruth === 'library_catalog' ||
    compactPriceSource === 'LIBRARY_CATALOG' ||
    compactPriceSource === 'SMART_SEARCH_DISH_FALLBACK' ||
    compactPriceSource === 'FALLBACK' ||
    !hasSnapshotBreakdown;

  useEffect(() => {
    let cancelled = false;
    const numericRecipeId = Number(recipe?.id);
    const hasNativeCost = Number(displayedCostTotal || 0) > 0;
    if (!Number.isFinite(numericRecipeId) || (!shouldHydrateCost && hasNativeCost)) {
      setHydratedCostTotal(null);
      return () => { cancelled = true; };
    }

    const cacheKey = `${numericRecipeId}:${currentPortion}`;
    if (costHydrationCache.has(cacheKey)) {
      setHydratedCostTotal(costHydrationCache.get(cacheKey));
      return () => { cancelled = true; };
    }

    const token = localStorage.getItem('authToken');
    const detailPath = recipe?.sourceOfTruth === 'library_catalog'
      ? `/api/recipes/library/${numericRecipeId}?portion=${Number(currentPortion) || 1}`
      : `/api/recipes/${numericRecipeId}?portion=${Number(currentPortion) || 1}`;
    fetch(`${API_BASE_URL}${detailPath}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((full) => {
        if (cancelled || !full) return;
        const total = Number(
          full?.priceSnapshot?.total ??
          full?.presentation?.totalCost ??
          full?.totalCost ??
          0
        );
        if (Number.isFinite(total) && total > 0) {
          costHydrationCache.set(cacheKey, total);
          setHydratedCostTotal(total);
        }
      })
      .catch(() => {
        // Silent fallback: keep compact payload cost.
      });

    return () => { cancelled = true; };
  }, [recipe?.id, recipe?.sourceOfTruth, currentPortion, displayedCostTotal, shouldHydrateCost]);

  // ⏱️ Professional Non-Linear Time Scaling Model (V12.110)
  const calculateScaledTime = () => {
    const fixed = parseInt(recipe.fixedTime) || 10;
    const variable = parseInt(recipe.variableTime) || 15;
    const exponent = parseFloat(recipe.scalingModel?.exponent) || 0.7;

    const baseForTime = isPreScaled ? appliedPortion : baseSrv;
    if (currentPortion === baseForTime) return Math.round(fixed + variable);

    const scaleFactor = Math.pow(currentPortion / Math.max(1, baseForTime), exponent);
    return Math.round(fixed + (variable * scaleFactor));
  };

  const rawTimeValue = String(cookingTime || cookTime || 25).trim().toLowerCase();
  const parsedRawTime = Number.parseInt(rawTimeValue, 10);
  const timeByLevel = rawTimeValue.includes('high') ? 50 : (rawTimeValue.includes('medium') ? 35 : (rawTimeValue.includes('low') ? 20 : 25));
  const fallbackRawTime = Number.isFinite(parsedRawTime) ? parsedRawTime : timeByLevel;
  const displayCookingTime = Math.max(calculateScaledTime(), fallbackRawTime);
  const effectiveDisplayedCost = Number(hydratedCostTotal || 0) > 0
    ? Number(hydratedCostTotal || 0)
    : (Number(displayedCostTotal || 0) > 0 ? Number(displayedCostTotal || 0) : 0);
  const detectMealType = () => {
    const tokens = [
      recipe?.serveStyle,
      recipe?.category,
      recipe?.mealType,
      recipe?.dishType,
      recipe?.recipeType,
      recipe?.name,
      recipe?.title
    ]
      .map((x) => String(x || '').toLocaleLowerCase('tr-TR'))
      .join(' ');
    if (/(corba|çorba|soup|sulu)/.test(tokens)) return 'soup';
    if (/(salata|salad|meze)/.test(tokens)) return 'salad';
    if (/(ana yemek|anayemek|main|yemek|et|tavuk)/.test(tokens)) return 'main';
    return 'other';
  };
  const mealType = detectMealType();
  const outsideMultiplierByType = {
    soup: 2.2,
    main: 2.9,
    salad: 2.0,
    other: 2.5
  };
  const inferredOutsideFallback = effectiveDisplayedCost * (outsideMultiplierByType[mealType] || outsideMultiplierByType.other);
  const inferredOutsideCost = Number(
    recipe?.priceSnapshot?.outsidePrice ??
    recipe?.presentation?.outsidePrice ??
    inferredOutsideFallback
  ) || 0;
  const roundedDisplayedCost = Math.round(Number(effectiveDisplayedCost || 0));
  const roundedOutsideCost = Math.round(Number(inferredOutsideCost || 0));
  const pocketSaving = Math.max(0, inferredOutsideCost - effectiveDisplayedCost);
  const roundedSaving = Math.round(pocketSaving);

  return (
    <div
      className={`group flex flex-col rounded-[2.5rem] transition-all duration-300 cursor-pointer relative top-0 hover:-top-1 hover:shadow-2xl overflow-hidden border ${currentStyles[activeState]} ${isSelected ? 'ring-2 ring-rose-500 border-rose-500' : ''} ${isAdaptiveRecipe ? 'border-amber-400 dark:border-amber-500 shadow-[0_0_0_1px_rgba(251,191,36,0.22),0_16px_40px_rgba(245,158,11,0.18)] dark:shadow-[0_0_0_1px_rgba(245,158,11,0.28),0_18px_44px_rgba(245,158,11,0.12)]' : ''}`}
      onClick={handleCardClick}
    >
      {/* ⚡ SMART INSIGHT SECTION (Akıllı Öneri) - Conditional Rendering */}
      {showSmartHeader && (
        <div className={`px-5 py-3.5 flex flex-col gap-1.5 ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-amber-500 text-[18px] font-black">bolt</span>
              <span className={`text-[11px] font-black tracking-widest ${isDarkMode ? 'text-amber-200' : 'text-amber-600'}`}>[AKILLI ÖNERİ]</span>
            </div>
            {recoveryAmount > 0 && (
              <div className={`px-2 py-0.5 rounded-full text-[10px] font-black flex items-center gap-1 ${isDarkMode ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'}`}>
                <span className="text-[12px]">+</span> {Math.floor(recoveryAmount)} ₺ Kurtar
              </div>
            )}
          </div>

          {isRescue && (
            <div className="flex items-center gap-1.5 px-0.5 mt-0.5 animate-in fade-in slide-in-from-left-2 duration-700">
              <div className="size-5 rounded-full bg-rose-500/10 flex items-center justify-center text-rose-500">
                <span className="material-symbols-outlined text-[14px]">timer</span>
              </div>
              <p className={`text-[10px] font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                SKT: <span className="text-rose-500 font-extrabold">{rescueDaysLeft} gün</span> kalan ürünler kullanılır
              </p>
            </div>
          )}
        </div>
        )}
      )}

      {/* 🖼️ IMAGE CONTAINER */}
      <div className={`relative ${isCompact ? 'hidden' : 'aspect-[1.5/1] w-full'} group-hover:brightness-110 transition-all duration-500`}>
        {/* Separator Line */}
        <div className={`absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-rose-500/30 to-transparent z-10 ${!showSmartHeader ? 'hidden' : ''}`}></div>

        {/* 🥘 AVAILABILITY (make-now stok durumu) rozeti */}
        {typeof recipe.availabilityScore === 'number' && recipe.availabilityLabel && (
          <div
            className={`absolute top-3 left-3 z-20 px-2.5 py-1 rounded-full text-[10px] font-black shadow-lg backdrop-blur-md border flex items-center gap-1 ${
              recipe.availabilityScore >= 1
                ? 'bg-emerald-500/90 text-white border-emerald-200/40'
                : recipe.availabilityScore >= 0.75
                  ? 'bg-lime-500/90 text-white border-lime-200/40'
                  : recipe.availabilityScore >= 0.5
                    ? 'bg-amber-500/90 text-white border-amber-200/40'
                    : 'bg-rose-500/90 text-white border-rose-200/40'
            }`}
            title={recipe.availabilityLabel}
          >
            <span className="material-symbols-outlined text-[12px]">
              {recipe.availabilityScore >= 1 ? 'check_circle'
                : recipe.availabilityScore >= 0.75 ? 'kitchen'
                  : recipe.availabilityScore >= 0.5 ? 'shopping_cart'
                    : 'error'}
            </span>
            {recipe.availabilityLabel}
          </div>
        )}

        {imageUrl ? (
          <img
            src={resolveImageSrc(imageUrl)}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-4xl">🍽️</div>
        )}
        {/* 👶 BABY FRIENDLY TAG */}
        {recipe.isChildFriendly && (userProfile?.hasBaby || userProfile?.bebek_var_mi) && (
          <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-2xl bg-[#1e293b]/80 backdrop-blur-md border border-white/5 flex items-center gap-1.5 text-white/90 text-[10px] font-black shadow-xl">
            <span className="material-symbols-outlined text-[14px] text-blue-400">baby_changing_station</span>
            Bebek Dostu
          </div>
        )}

        {isAdaptiveRecipe && (
          <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-2xl bg-amber-500/90 backdrop-blur-md border border-amber-200/40 flex items-center gap-1.5 text-white text-[10px] font-black shadow-xl">
            <span className="material-symbols-outlined text-[14px]">autorenew</span>
            Alternatifli Uyum
          </div>
        )}

        {/* ❤️ FAVORITE BUTTON */}
        {onToggleSelect && (
          <div
            onClick={handleCheckboxClick}
            className={`absolute top-3 right-3 rounded-full z-40 cursor-pointer flex items-center justify-center transition-all size-9 text-[18px] bg-white/10 backdrop-blur-md border border-white/20 group-hover:bg-rose-500 transition-colors shadow-2xl ${isSelected ? 'bg-rose-500 border-rose-400 text-white' : 'text-white/60 hover:text-white'}`}
          >
            <span className={`material-symbols-outlined font-black transition-all ${isSelected ? 'scale-110' : ''}`} style={{ fontVariationSettings: isSelected ? "'FILL' 1" : "'FILL' 0" }}>
              favorite
            </span>
          </div>
        )}
      </div>

      {/* 📝 CONTENT SECTION */}
      <div className="flex flex-col p-5 pb-6 gap-3">
        {missingCount > 0 ? (
          <div className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wider ${isDarkMode ? 'bg-red-500/15 text-red-300 border border-red-500/30' : 'bg-red-50 text-red-600 border border-red-200'
            }`}>
            <span className="material-symbols-outlined text-[12px]">shopping_cart</span>
            {missingCount} MALZEME EKSİK
          </div>
        ) : (
          <div className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wider ${isDarkMode ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            }`}>
            <span className="material-symbols-outlined text-[12px]">local_fire_department</span>
            BUGÜN YAP
          </div>
        )}
        <h4 className={`font-black tracking-tight ${isCompact ? 'text-[15px]' : 'text-xl'} text-slate-800 dark:text-white leading-tight line-clamp-2 mb-1`}>
          {title}
        </h4>

        {!hideSavingsPanel && (<>
        <div className={`rounded-xl border px-3 py-2 ${pocketSaving > 0
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300'
          : 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300'
          }`}>
          <div className="text-lg font-black leading-none">+{roundedSaving} ₺ cebinde kalır</div>
          <div className="mt-1 text-[10px] font-semibold opacity-80">Dışarıdan yemek yerine evde yaparsan</div>
        </div>

        {/* 🏷️ REDESIGNED BADGES (Separate Row, Aligned Edges) */}
        </>)}
        <div className="flex flex-wrap items-center gap-1.5 -ml-1.5 mt-0.5">
          {difficulty && (
            <div className={`px-1.5 py-0.5 rounded-md flex items-center gap-1 border ${difficulty === 'Zor' ? 'bg-red-500/5 text-red-600 border-red-200/50' :
              difficulty === 'Orta' ? 'bg-amber-500/5 text-amber-600 border-amber-200/50' :
                'bg-emerald-500/5 text-emerald-600 border-emerald-200/50'
              }`}>
              <span className="material-symbols-outlined text-[11px] font-bold text-inherit">speed</span>
              <span className="text-[8px] font-bold uppercase tracking-tight text-inherit">{difficulty}</span>
            </div>
          )}

          <div className={`px-1.5 py-0.5 rounded-md flex items-center gap-1 border ${currentIsFullMatch ? 'bg-emerald-500/10 text-emerald-600 border-emerald-400/20' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
            <span className={`material-symbols-outlined text-[11px] font-bold ${currentIsFullMatch ? 'text-emerald-600' : 'text-slate-400'}`}>{currentIsFullMatch ? 'check_circle' : 'equalizer'}</span>
            <span className={`text-[8px] font-bold tracking-tight text-slate-600`}>%{currentMatchPerc} Uyum</span>
          </div>

          {((resolvedMatch.isAllergenSafe === false) || recipe.isAllergenSafe === false || recipe._hasAllergenSafe === false || recipe._hasAllergenCollision) && (
            userProfile?.hasAllergenPremium ? (
              <div className="px-1.5 py-0.5 rounded-md flex items-center gap-1 bg-red-600 text-white shadow-sm">
                <span className="material-symbols-outlined text-white text-[11px] font-bold">report</span>
                <span className="text-[8px] font-bold uppercase tracking-tight">Alerjen</span>
              </div>
            ) : (
              <div className="px-1 py-0.5 rounded-md flex items-center justify-center bg-slate-100 border border-slate-200 text-slate-500">
                <span className="material-symbols-outlined text-[12px] font-bold">verified_user</span>
              </div>
            )
          )}

          {healthBadges.slice(0, 2).map((badge, index) => (
            <div
              key={`health-badge-${index}-${badge}`}
              className="px-1.5 py-0.5 rounded-md flex items-center gap-1 border bg-blue-500/10 text-blue-600 border-blue-300/40"
            >
              <span className="material-symbols-outlined text-[11px] font-bold text-blue-600">verified</span>
              <span className="text-[8px] font-bold tracking-tight">{badge}</span>
            </div>
          ))}
        </div>

        {healthReason && !healthWarning?.conflict && (
          <div className={`mt-1 px-2 py-1.5 rounded-lg border text-[10px] font-semibold ${isDarkMode
            ? 'bg-blue-500/10 border-blue-500/20 text-blue-200'
            : 'bg-blue-50 border-blue-100 text-blue-700'
            }`}>
            Neden önerildi: {healthReason}
          </div>
        )}

        {/* Faz 4: Sağlık+ uyarısı — çelişki varsa amber, engellemez */}
        {healthWarning?.conflict && (
          <div className={`mt-1 px-2 py-1.5 rounded-lg border text-[10px] font-semibold flex items-start gap-1.5 ${isDarkMode
            ? 'bg-amber-500/10 border-amber-500/20 text-amber-200'
            : 'bg-amber-50 border-amber-200 text-amber-700'
            }`}>
            <span className="material-symbols-outlined text-[12px] mt-px">info</span>
            <span>{healthWarning.message}</span>
          </div>
        )}

        {/* Stats Row */}
        <div className="flex items-center gap-3 opacity-60">
          <div className="flex items-center gap-1 text-[11px] font-black tracking-wide text-slate-500 dark:text-slate-400">
            <span className="material-symbols-outlined text-[15px]">schedule</span>
            {displayCookingTime} dk
          </div>
          <div className="size-1 rounded-full bg-slate-300 dark:bg-slate-700"></div>
          <div className="flex items-center gap-1 text-[11px] font-black tracking-wide text-slate-500 dark:text-slate-400 whitespace-nowrap">
            {displayPortionLabel} Porsiyon
          </div>
          <div className="size-1 rounded-full bg-slate-300 dark:bg-slate-700"></div>
          <div className="flex items-center gap-1 text-[11px] font-black tracking-wide text-slate-500 dark:text-slate-400">
            {Math.round((parseInt(calories) || 450) * multiplier)} kcal
          </div>
        </div>

        {/* 🚀 ACTION BUTTONS SECTION */}

        {/* Home vs Outside Cost */}
        {!hideCostComparison && (<>
        <div className="mt-1 rounded-lg border border-slate-200/60 bg-slate-50/70 px-2.5 py-2 dark:border-white/10 dark:bg-white/5">
          <div className="flex items-center justify-between text-[11px] font-black">
            <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>Evde</span>
            <span className="text-rose-500">{Math.round(roundedDisplayedCost)} ₺</span>
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[11px] font-black">
            <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>Dışarıda</span>
            <span className={isDarkMode ? 'text-slate-200' : 'text-slate-700'}>~{Math.round(roundedOutsideCost)} ₺</span>
          </div>
        </div>

        {/* 🚀 ACTION BUTTONS SECTION */}
        </>)}
        {!hideActionButtons && (
          <div className="flex items-center gap-2.5 mt-2.5">
          {!hidePrimaryAction && (
            <button
              className={`flex-[1.5] py-3.5 rounded-2xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-[0.98] group/btn ${onToggleSelect
                ? (isSelected
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/20'
                  : 'bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white shadow-rose-500/20')
                : 'bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white shadow-rose-500/20'
                }`}
              onClick={onToggleSelect ? handleCheckboxClick : handleCardClick}
            >
              <span className="material-symbols-outlined text-[18px] font-black group-hover/btn:rotate-12 transition-transform">
                {onToggleSelect ? (isSelected ? 'check_circle' : 'add_task') : 'bolt'}
              </span>
              <span className="text-[12px] font-extrabold uppercase tracking-widest text-white">
                {onToggleSelect ? (isSelected ? 'Seçildi' : 'Seç') : 'Kullandım'}
              </span>
            </button>
          )}

          <button
            className={`${hidePrimaryAction ? 'w-full' : 'flex-1'} py-3.5 rounded-2xl flex items-center justify-center gap-1.5 transition-colors border group/detail active:scale-[0.98] ${isDarkMode ? 'bg-white/5 border-white/5 hover:bg-white/10 text-white/70 hover:text-white' : 'bg-slate-50 border-slate-100 hover:bg-slate-100 text-slate-500 hover:text-slate-800'}`}
            onClick={handleCardClick}
          >
            <span className="text-[11px] font-black tracking-tight">Detaylar</span>
            <span className="material-symbols-outlined text-[14px] font-black group-hover/detail:translate-x-0.5 transition-transform">chevron_right</span>
          </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipeCard;
